import {
  Autocomplete,
  Box,
  BoxProps,
  Checkbox,
  Chip,
  createFilterOptions,
  MenuItem,
  Paper,
  SelectProps,
  TextField,
  Typography,
} from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cast } from '~/utils/helper';

export type BaseSelectProps<T extends Record<string, any>> = BoxProps &
  SelectProps & {
    value?: any;
    valueDisplay?: any;
    label?: string | undefined;
    placeholder?: string | undefined;
    renderSelected?: (item: T) => React.ReactElement;
    onChange?: (event: any) => void;
    options?: Array<T>;
    renderOptionItem?: (item: T, index: number) => React.ReactElement;
    menuClassName?: string;
    isSearchEnable?: boolean;
    isHideIcon?: boolean;
    bottomComponent?: () => React.ReactElement;
    topComponent?: () => React.ReactElement;
    helperText?: string;
    onSearchChange?: (...event: any[]) => void;
    typeOption?: string;
    selectedInOption?: boolean;
    onOpen?: (status: boolean) => void;
    isDisplayAllOption?: boolean;
    displayInvalidValue?: boolean; // In the case value doesn't match any options, display the value instead default
    showNumberOption?: number;
    enterValue?: boolean;
    disableClearable?: boolean;
    menuItemWidth?: number;
    showIcon?: boolean;
    deboundAction?: (value: string) => void;
    timeout?: number;
  };

const BaseAutocomplete = <T extends Record<string, any>>(props: BaseSelectProps<T>) => {
  const {
    isHideIcon = false,
    isSearchEnable = false,
    value,
    label,
    placeholder,
    renderSelected,
    onChange,
    options,
    className,
    typeOption,
    disabled = false,
    isDisplayAllOption = false,
    disableClearable = true,
    showIcon = true,
    displayInvalidValue = false,
    timeout = 1000,
    ...restProps
  }: BaseSelectProps<T> = props;

  const [textSearch, setTextSearch] = useState<string>('');
  const { t } = useTranslation('admin/component');
  useEffect(() => {
    if (props.deboundAction) {
      const handler = setTimeout(() => {
        if (props.deboundAction) props.deboundAction(textSearch);
      }, timeout);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [textSearch, timeout]);
  const filter = createFilterOptions<any>({
    ignoreCase: true,
  });
  const renderOptionItem =
    props.renderOptionItem ||
    function (option: T, _index: number) {
      let isChecked = value === option.id;
      if (Array.isArray(value)) isChecked = value.indexOf(option.id) > -1;
      return (
        <Box key={option.id} className='select-option'>
          {option.name}
        </Box>
      );
    };

  const renderSelectedItem =
    renderSelected ||
    function (selectedItem) {
      if (typeOption === 'date') {
        const date = new Date(selectedItem.id).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
        return <Box className='p-[15px]'>{date}</Box>;
      }
      if (Array.isArray(selectedItem)) {
        const selectedComponent = selectedItem.map((item) => {
          return <Chip key={item.id} label={item.name} variant='outlined' size='small' className='mr-[5px]' />;
        });
        return <Box className='p-[15px]'>{selectedComponent}</Box>;
      }
      return <Box className='p-[15px] truncate text-black'>{selectedItem.name}</Box>;
    };

  const renderValue = () => {
    if (value !== undefined) {
      let selectedItem: any = null;
      if (Array.isArray(value)) {
        selectedItem = [];
        for (let i in value) {
          let matchedOption = props.options?.find((item) => item.id === value[i]);
          if (matchedOption) selectedItem.push(matchedOption);
        }
      } else {
        selectedItem = props.options?.find((item) => item.id === value);
      }

      return selectedItem ? (
        renderSelectedItem(selectedItem)
      ) : props.displayInvalidValue || props.enterValue ? (
        props.valueDisplay ? (
          <Box className='pl-[15px] pr-[25px] truncate'>{props.valueDisplay}</Box>
        ) : (
          <Box className='pl-[15px] pr-[25px] truncate'>{value}</Box>
        )
      ) : (
        <Box className='pl-[15px] pr-[25px] text-[#b0b4bd] truncate'>{placeholder ?? label}</Box>
      );
    }
    return (
      <Typography className='flex px-[15px] flex-1 select-none text-[14px]/[16.8px] text-[#A2A7B4] font-normal'>
        {placeholder ?? label ?? ''}
      </Typography>
    );
  };

  return (
    <Autocomplete
      id='base-autocomplete'
      disableClearable={disableClearable}
      options={options ?? []}
      defaultValue={props.defaultValue}
      getOptionLabel={(option) => (typeof option === 'string' ? option : cast(option.name) || '')}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      onChange={(event, newValue, reason) => {
        let actualValueToSend: string | number | null = null;
        if (newValue) {
          actualValueToSend = newValue.id;
        }

        const syntheticEvent: React.ChangeEvent<{ name?: string; value: unknown }> = {
          target: {
            name: props.name,
            value: actualValueToSend,
          },
        } as React.ChangeEvent<{ name?: string; value: unknown }>;

        onChange && onChange(syntheticEvent);
      }}
      value={value ?? ''}
      freeSolo={props.enterValue}
      // eslint-disable-next-line react/jsx-no-duplicate-props
      disablePortal={false}
      forcePopupIcon={showIcon} // show icon dropdown
      renderOption={(optionProps, option) => {
        const findOption = options?.find((item) => option.id === item.id && option.name?.trim() === item.name);

        if (!findOption && option.key) {
          return (
            <MenuItem {...optionProps} sx={{ fontSize: '13px' }}>
              {option.key}
            </MenuItem>
          );
        }

        return (
          <MenuItem {...optionProps} sx={{ fontSize: '13px' }}>
            {renderOptionItem(option, option.key)}
          </MenuItem>
        );
      }}
      PaperComponent={(paperProps) => (
        <Paper {...paperProps} sx={{ width: props.menuItemWidth ?? '100%', minWidth: 200 }} />
      )}
      disabled={disabled}
      readOnly={restProps.readOnly}
      filterOptions={(options, params) => {
        let filtered = filter(options, params);

        const { inputValue } = params;
        const isExist = filtered.find((item) => inputValue === item.name);
        if (props.isDisplayAllOption) {
          filtered = [
            ...[
              {
                id: '',
                name: t('baseSelect.all'),
              } as any,
            ],
            ...filtered,
          ];
        }
        if (inputValue !== '' && inputValue !== isExist?.name) {
          if (props.displayInvalidValue) {
            filtered = [
              ...[
                {
                  id: inputValue,
                  name: inputValue,
                } as any,
              ],
              ...filtered,
            ];
          }
        }
        if (props.showNumberOption && typeof props.showNumberOption === 'number' && props.showNumberOption > 0) {
          return filtered.slice(0, props.showNumberOption);
        }

        return filtered;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          value={renderValue()}
          label={props.label}
          className={props.className}
          placeholder={placeholder}
          variant='outlined'
          onChange={(e) => {
            setTextSearch(e.target.value);
          }}
        />
      )}
    />
  );
};

export default memo(BaseAutocomplete);
